/**
 * Settings — appearance (light/dark/auto), table density, account, about.
 * Preferences are per-device (localStorage); stated in-UI.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Moon, Sun, MonitorSmartphone } from 'lucide-react';
import { useAppearance, type AppearanceSetting, type Density } from '../theme/AppearanceContext';
import { useAuth } from '../auth/AuthProvider';
import { PORTAL_META } from '../registry/portalMeta';

export const Settings: React.FC = () => {
  const { density, mode, setDensity, setMode } = useAppearance();
  const { user } = useAuth();

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Settings
      </Typography>

      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Appearance
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_e, v: AppearanceSetting | null) => { if (v) setMode(v); }}
              aria-label="Appearance mode"
            >
              <ToggleButton value="light" aria-label="Light mode">
                <Sun size={16} style={{ marginRight: 6 }} /> Light
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Dark mode">
                <Moon size={16} style={{ marginRight: 6 }} /> Dark
              </ToggleButton>
              <ToggleButton value="auto" aria-label="Auto mode">
                <MonitorSmartphone size={16} style={{ marginRight: 6 }} /> Auto
              </ToggleButton>
            </ToggleButtonGroup>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Table density
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={density}
              onChange={(_e, v: Density | null) => { if (v) setDensity(v); }}
              aria-label="Table density"
            >
              <ToggleButton value="compact">Compact</ToggleButton>
              <ToggleButton value="comfortable">Comfortable</ToggleButton>
              <ToggleButton value="wide">Wide</ToggleButton>
            </ToggleButtonGroup>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Account
            </Typography>
            <Typography variant="body2">Username: {user?.username ?? '—'}</Typography>
            <Typography variant="body2">Role: {user?.role ?? '—'}</Typography>
            <Typography variant="body2">Tenant: {user?.tenant_id ?? '—'}</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              About
            </Typography>
            <Typography variant="body2">{PORTAL_META.platformTitle} Portal</Typography>
            <Typography variant="caption" color="text.secondary">
              Appearance and density preferences are stored on this device only.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default Settings;
